import type { ReactNode } from 'react';

import AddchartIcon from '@mui/icons-material/Addchart';
import BalanceRoundedIcon from '@mui/icons-material/BalanceRounded';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { convertNumber, convertValue } from 'src/utils/number';

import type { FundMovementsSummary } from './types';

export interface SummaryValues {
  purchases: number;
  sales: number;
  net: number;
}

export interface SummaryCardsProps {
  summary?: FundMovementsSummary | null;
  totals?: SummaryValues | null;
  showShares?: boolean;
}

interface SummaryCardProps {
  title: string;
  totalValue: number;
  shareQuantity?: number;
  showShares?: boolean;
  valueColor: string;
  titleColor?: string;
  icon: ReactNode;
}

function SummaryCard({
  title,
  totalValue,
  shareQuantity,
  showShares = false,
  valueColor,
  titleColor = 'text.secondary',
  icon,
}: SummaryCardProps) {
  return (
    <Card
      elevation={1}
      sx={{
        flex: 1,
        minWidth: 160,
        borderRadius: 2,
      }}>
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 0.5,
          }}>
          {icon}
          <Typography
            variant="caption"
            sx={{
              color: titleColor,
              textTransform: 'uppercase',
              fontWeight: 600,
              letterSpacing: 0.5,
            }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h5" fontWeight="bold" sx={{ color: valueColor }}>
          {convertValue(totalValue)}
        </Typography>
        {showShares && shareQuantity !== undefined && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {convertNumber(shareQuantity)} cotas
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export function SummaryCards({
  summary,
  totals,
  showShares = false,
}: SummaryCardsProps) {
  const theme = useTheme();

  const purchasesValue =
    totals?.purchases ?? summary?.purchases?.totalValue ?? 0;
  const salesValue = totals?.sales ?? summary?.sales?.totalValue ?? 0;
  const netValue = totals?.net ?? summary?.net?.totalValue ?? 0;

  const purchasesShares = summary?.purchases?.shareQuantity ?? 0;
  const salesShares = summary?.sales?.shareQuantity ?? 0;
  const netShares = summary?.net?.shareQuantity ?? 0;

  const netTotalValueColor =
    netValue >= 0 ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        flexWrap: 'wrap',
      }}>
      <SummaryCard
        title="Compras"
        totalValue={purchasesValue}
        shareQuantity={purchasesShares}
        showShares={showShares}
        valueColor={theme.palette.success.main}
        icon={
          <AddchartIcon
            fontSize="small"
            sx={{ color: theme.palette.success.main }}
            aria-hidden
          />
        }
      />
      <SummaryCard
        title="Vendas"
        totalValue={salesValue}
        shareQuantity={salesShares}
        showShares={showShares}
        valueColor={theme.palette.error.main}
        icon={
          <RemoveCircleOutlineOutlinedIcon
            fontSize="small"
            sx={{ color: theme.palette.error.main }}
            aria-hidden
          />
        }
      />
      <SummaryCard
        title="Valor líquido"
        totalValue={netValue}
        shareQuantity={netShares}
        showShares={showShares}
        valueColor={netTotalValueColor}
        titleColor={theme.palette.info.main}
        icon={
          <BalanceRoundedIcon
            fontSize="small"
            sx={{ color: theme.palette.info.main }}
            aria-hidden
          />
        }
      />
    </Box>
  );
}
